import { useContext, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { usePaymentMethods } from '@/hooks/usePaymentMethods';
import { useOrganization } from '@/hooks/useOrganization';
import { Button } from '@/components/ui/button';
import { formatCurrency } from '@/lib/currency';
import { isPosSelectablePaymentMethod } from '@/constants/defaultPaymentMethodContant';
import { convertToDollars, isDollarPaymentMethod } from '@/utils/paymentUtils';
import { LanguageProviderContext } from '@/providers/LanguageProvider';

interface PaymentMethodSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (paymentMethodId: string) => void;
  total: number;
}

export const PaymentMethodSelectorModal = ({
  isOpen,
  onClose,
  onSelect,
  total,
}: PaymentMethodSelectorModalProps) => {
  const { translator } = useContext(LanguageProviderContext);
  const { currentOrganization } = useOrganization();
  const { paymentMethods } = usePaymentMethods();
  const [selected, setSelected] = useState<string | null>(null);

  const currency = currentOrganization?.currency ?? 'CRC';
  const exchangeRate = currentOrganization?.exchangeRate || 0;
  const activePaymentMethods = paymentMethods.filter(isPosSelectablePaymentMethod);
  const selectedMethod = activePaymentMethods.find((method) => method.id === selected);
  const showDollarTotal = selectedMethod ? isDollarPaymentMethod(selectedMethod) : false;
  const totalInDollars = showDollarTotal ? convertToDollars(total, exchangeRate) : null;

  const handleConfirm = () => {
    if (selected) {
      onSelect(selected);
    }
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-60 bg-black/50" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-60 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg bg-card p-0 shadow-lg">
          <div className="flex items-center justify-between border-b p-6">
            <Dialog.Title className="text-xl font-bold">
              {translator('ExternalOrders.paymentMethod')}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                type="button"
                className="text-muted-foreground hover:text-foreground"
                aria-label={translator('Global.close')}
              >
                <X className="h-5 w-5" />
              </button>
            </Dialog.Close>
          </div>

          <div className="p-6">
            <p className={`text-lg font-semibold ${totalInDollars == null ? 'mb-4' : 'mb-1'}`}>
              {translator('Global.total')}: {formatCurrency(total, currency)}
            </p>
            {totalInDollars != null && (
              <p className="mb-4 text-sm text-muted-foreground">
                ≈ {formatCurrency(totalInDollars, 'USD')}
              </p>
            )}

            <div className="mb-6 space-y-2">
              {activePaymentMethods.map((method) => (
                <button
                  key={method.id}
                  type="button"
                  onClick={() => setSelected(method.id)}
                  className={`w-full rounded-lg border p-4 text-left transition-colors ${
                    selected === method.id
                      ? 'border-primary bg-primary/10'
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <span className="font-medium">{method.name}</span>
                </button>
              ))}
            </div>

            <Button
              type="button"
              onClick={handleConfirm}
              disabled={!selected}
              className="w-full"
            >
              {translator('ExternalOrders.confirmComplete')}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
